import { useContext, useEffect } from 'react';

import { HOME_OVERVIEW_RECENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';

export const HOME_OVERVIEW_RECENT_LEFT_HOME_REASON = 'User navigated away from Home';

/**
 * Aborts the `home/overview/recent` run started by this For You mount if it is still open when the
 * user leaves Home. Leaving mid-load is a user cancellation, so the timeout check is skipped:
 * without this, a run left open past the 20s timeout is recorded as a timeout failure.
 *
 * Must be called in the same component that renders the `ExperienceStart`, which starts the run
 * during render, so the run is current by the time the mount effect captures its `taskId`.
 */
export const useAbortHomeOverviewRecentOnLeave = (): void => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		const taskId = experienceTracker.getExperienceState(HOME_OVERVIEW_RECENT_EXPERIENCE)?.taskId;

		return () => {
			// A remounted For You starts its replacement run during render, before this cleanup runs,
			// so only abort when the current run is still the one this mount started.
			if (
				!taskId ||
				experienceTracker.getExperienceState(HOME_OVERVIEW_RECENT_EXPERIENCE)?.taskId !== taskId ||
				!fg('confluence_home_recent_experience_leak_fix')
			) {
				return;
			}

			experienceTracker.abort({
				name: HOME_OVERVIEW_RECENT_EXPERIENCE,
				reason: HOME_OVERVIEW_RECENT_LEFT_HOME_REASON,
				checkForTimeout: false,
			});
		};
	}, [experienceTracker]);
};
